import {intersection, uniq} from 'lodash'
import {EnvironmentProvider} from 'providers/EnvironmentProvider'
import {useMemo} from 'react'
import stringFormat from 'string-format'
import {Ventrata} from 'types'
import {Alcatraz} from './Alcatraz'

const KNOWN_WIDGETS: Record<string, (props: any) => JSX.Element> = {
  Alcatraz,
}

type Props = {
  order: Ventrata.Order | null
  className?: string
}

export const ConfirmationPageSubtitle = ({order}: Props) => {
  const variations = EnvironmentProvider.useVariations()

  const widgetData = useMemo(() => {
    const usedWidgetCodes = Object.keys(variations?.conditions ?? {}).filter((code) =>
      code.startsWith('confirmationPageSubtitle')
    )

    const usedProductIds = uniq(order?.bookings.map((booking) => booking.product.id))

    if (usedWidgetCodes.length === 0) {
      return null
    }

    if (usedWidgetCodes.length > 1) {
      console.warn(`Multiple confirmation page subtitle widgets found, code '${usedWidgetCodes}'`)
    }

    const widgetCode = usedWidgetCodes[0]

    const widgetId = widgetCode.split(':')[1]

    if (!widgetId) {
      console.warn(`Invalid widget code, code '${widgetCode}'`)
      return null
    }

    const widget = KNOWN_WIDGETS[widgetId]

    if (!widget) {
      console.warn(`Unknown widget, code '${widgetCode}'`)
      return null
    }

    const conditionConfig = variations?.conditions[widgetCode]

    if (!Array.isArray(conditionConfig)) {
      console.warn(`Invalid widget condition config, code '${widgetCode}'`)
      return null
    }

    if (intersection(conditionConfig, usedProductIds).length === 0) {
      return null
    }

    const widgetConfig = variations?.widgets[widgetCode]

    if (!widgetConfig) {
      console.warn(`Unknown widget config, code '${widgetCode}'`)
      return null
    }

    return {
      Component: widget,
      config: widgetConfig,
      code: widgetCode,
    }
  }, [variations, order])

  if (!order || !variations) {
    return null
  }

  if (!widgetData) {
    return (
      <>
        {stringFormat(
          `We've sent your tour confirmation and details to {order.contact.emailAddress}. We look forward to seeing you on the tour!`,
          {order}
        )}
      </>
    )
  }

  const {Component, config} = widgetData

  return <Component config={config} order={order} />
}
